import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  Key,
  Copy,
  Trash,
  Archive,
  ArrowUpRight,
  Plus,
  WarningCircle,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function normalizePlanKey(plan) {
  const value = String(plan || "starter").trim().toLowerCase();
  return value === "free" ? "starter" : value;
}

function firstNumber(...values) {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }

  return null;
}

function usageLimitValue(usage, key, fallback = null) {
  return firstNumber(
    usage?.[key],
    usage?.limits?.[key],
    usage?.account_limits?.[key],
    usage?.plan_limits?.[key],
    fallback
  );
}

function percentOf(used, allowed) {
  if (allowed === null || allowed === undefined || allowed === 0) return 0;
  return Math.max(0, Math.min(100, Math.round((Number(used || 0) / Number(allowed)) * 100)));
}

function projectStatus(project) {
  return String(project?.status || "active").trim().toLowerCase();
}

function isArchivedProject(project) {
  return ["archived", "deleted"].includes(projectStatus(project));
}

function projectLimitForUsage(usage) {
  const planKey = normalizePlanKey(usage?.plan);
  return firstNumber(
    usage?.projects?.allowed,
    usage?.projects?.limit,
    usage?.project_limit,
    usageLimitValue(usage, "project_limit", planKey === "pro" ? 10 : planKey === "growth" ? 3 : 1)
  );
}

function projectTypeLabel(value) {
  const clean = String(value || "marketing").trim();
  return clean ? clean.replace(/[_-]+/g, " ") : "marketing";
}

function projectDescription(project) {
  const settings = project?.settings && typeof project.settings === "object" ? project.settings : {};

  return String(
    project?.description ||
      project?.project_description ||
      settings?.description ||
      settings?.project_description ||
      settings?.business_description ||
      ""
  ).trim();
}

function getApiMessage(err, fallback = "Action failed.") {
  const detail = err?.response?.data?.detail || err?.response?.data?.error || err?.response?.data?.message;
  return typeof detail === "string" ? detail : fallback;
}

const TABS = [
  { key: "plan", label: "Plan & Usage" },
  { key: "team", label: "Team" },
  { key: "api", label: "API Keys" },
  { key: "account", label: "Account" },
];

function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  loading = false,
  onConfirm,
  onClose,
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center px-4"
      onClick={loading ? undefined : onClose}
    >
      <div
        className="w-full max-w-md bg-white border border-[#E5E7EB] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-5 border-b border-[#E5E7EB]">
          <div className="flex items-start gap-3">
            <div
              className={`h-10 w-10 flex items-center justify-center ${
                danger
                  ? "bg-[#FEF2F2] text-[#DC2626]"
                  : "bg-[#EFF6FF] text-[#002FA7]"
              }`}
            >
              <WarningCircle size={22} weight="duotone" />
            </div>

            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
                Confirm action
              </p>
              <h3 className="font-display text-2xl font-black mt-1">
                {title}
              </h3>
            </div>
          </div>
        </div>

        <div className="px-5 py-5">
          <p className="text-sm text-[#525252] leading-relaxed">
            {message}
          </p>
        </div>

        <div className="px-5 py-4 border-t border-[#E5E7EB] flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5] disabled:opacity-50"
          >
            {cancelLabel}
          </button>

          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
              danger
                ? "bg-[#DC2626] hover:bg-[#B91C1C]"
                : "bg-[#002FA7] hover:bg-[#002080]"
            }`}
          >
            {loading ? "Working..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "plan";
  const { user, usage, refreshUsage } = useAuth();

  useEffect(() => {
    refreshUsage();
  }, [refreshUsage]);

  const setTab = (t) => {
    params.set("tab", t);
    setParams(params, { replace: true });
  };

  return (
    <div>
      <section className="border-b border-[#E5E7EB]">
        <div className="container-wide py-10 md:py-14">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-3">
            Settings
          </p>

          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
            {user?.name}'s workspace.
          </h1>

          <p className="mt-3 text-[#525252] text-sm sm:text-base">
            {user?.email} · Plan:{" "}
            <span className="capitalize font-semibold text-[#0A0A0A]">
              {usage?.plan || user?.plan || "starter"}
            </span>
          </p>

          <nav
            className="mt-6 border-b border-[#E5E7EB] -mb-[1px] overflow-x-auto"
            data-testid="settings-tabs"
          >
            <div className="flex gap-1 min-w-max">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                    tab === t.key
                      ? "border-[#002FA7] text-[#0A0A0A]"
                      : "border-transparent text-[#737373] hover:text-[#0A0A0A]"
                  }`}
                  data-testid={`settings-tab-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </nav>
        </div>
      </section>

      <section className="container-wide py-10 md:py-14">
        {tab === "plan" && <PlanTab usage={usage} refreshUsage={refreshUsage} />}
        {tab === "team" && <TeamTab usage={usage} />}
        {tab === "api" && <ApiTab usage={usage} />}
        {tab === "account" && <AccountTab user={user} />}
      </section>
    </div>
  );
}

// ---------- Plan tab ----------
function PlanTab({ usage, refreshUsage }) {
  const [portalLoading, setPortalLoading] = useState(false);

  if (!usage) return <div className="h-24 bg-[#F5F5F5] animate-pulse" />;

  const planKey = normalizePlanKey(usage.plan);
  const pct = usage.cap ? Math.min(100, Math.round((usage.used / usage.cap) * 100)) : 0;
  const overCap = usage.cap !== null && usage.used >= usage.cap;
  const isPaid = planKey === "pro" || planKey === "growth";
  const projectUsed = firstNumber(usage.projects?.used, usage.project_count, usage.projects_used, 0) ?? 0;
  const projectAllowed = firstNumber(usage.projects?.allowed, usage.project_limit, usageLimitValue(usage, "project_limit", planKey === "pro" ? 10 : planKey === "growth" ? 3 : 1));
  const apiKeyUsed = firstNumber(usage.api_keys?.used, usage.api_keys_used, usage.api_key_count, 0) ?? 0;
  const apiKeyAllowed = firstNumber(usage.api_keys?.allowed, usage.api_key_limit, usageLimitValue(usage, "api_key_limit", planKey === "pro" ? 3 : 0));
  const proMetrics = [
    { label: "Generations", used: usage.used || 0, allowed: usage.cap, note: usage.cap === null ? "Unlimited" : `${usage.cap} / mo` },
    { label: "Websites / projects", used: projectUsed, allowed: projectAllowed, note: `${projectAllowed ?? 0} allowed` },
    { label: "Team seats", used: usage.seats?.used || 0, allowed: usage.seats?.allowed || 0, note: `${usage.seats?.allowed || 0} seats` },
    { label: "API keys", used: apiKeyUsed, allowed: apiKeyAllowed, note: `${apiKeyAllowed ?? 0} active keys` },
  ];

  const openBillingPortal = async () => {
    setPortalLoading(true);

    try {
      const { data } = await axios.post(`${API}/billing/portal`, {
        return_url: window.location.href,
      });

      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      toast.error(typeof detail === "string" ? detail : "Couldn't open billing portal.");
      setPortalLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="plan-tab">
      <div className="border border-[#E5E7EB] bg-white p-6 md:p-8">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
          Current plan
        </p>

        <h2 className="font-display text-3xl font-black capitalize mt-1">
          {usage.plan}
        </h2>

        <p className="text-[#525252] mt-2">
          {planKey === "starter" && "Free · SEO AI + Business Starter Kit · 50 generations / month · 1 website"}
          {planKey === "growth" && "Growth tools · 2,000 generations / month · unlimited private history · 3 websites"}
          {planKey === "pro" && "Pro tools · workflow automation · publishing · 3 team seats · API access"}
        </p>

        {isPaid ? (
          <button
            onClick={openBillingPortal}
            disabled={portalLoading}
            className="btn-primary mt-6 inline-flex"
            data-testid="manage-billing-btn"
          >
            {portalLoading ? "Opening…" : "Manage billing"}{" "}
            <ArrowUpRight size={16} weight="bold" />
          </button>
        ) : (
          <Link
            to="/pricing"
            className="btn-primary mt-6 inline-flex"
            data-testid="upgrade-plan-btn"
          >
            Upgrade <ArrowUpRight size={16} weight="bold" />
          </Link>
        )}
      </div>

      <div className="border border-[#E5E7EB] bg-white p-6 md:p-8">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
          Usage · {usage.month}
        </p>

        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-display text-4xl font-black">{usage.used}</span>
          <span className="text-[#737373]">
            {usage.cap === null ? "· unlimited" : `/ ${usage.cap} generations`}
          </span>
        </div>

        {usage.cap !== null && (
          <div className="mt-4 h-2 bg-[#F5F5F5] relative overflow-hidden" data-testid="usage-bar">
            <div
              className={`h-full ${overCap ? "bg-[#DC2626]" : "bg-[#002FA7]"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}

        {overCap && (
          <p className="mt-3 text-sm text-[#DC2626]">
            You've hit your monthly cap. Upgrade to keep shipping.
          </p>
        )}

        <div className="mt-5 pt-5 border-t border-[#E5E7EB] grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">
              Tools
            </p>
            <p className="mt-1 font-semibold">
              {usage.tools_allowed?.length || 0} unlocked
            </p>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">
              Seats
            </p>
            <p className="mt-1 font-semibold">
              {usage.seats?.used} / {usage.seats?.allowed} used
            </p>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">
              Websites
            </p>
            <p className="mt-1 font-semibold">
              {projectUsed} / {projectAllowed ?? "∞"} used
            </p>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">
              API keys
            </p>
            <p className="mt-1 font-semibold">
              {apiKeyUsed} / {apiKeyAllowed ?? 0} active
            </p>
          </div>
        </div>
      </div>

      {planKey === "pro" && (
        <div className="lg:col-span-2 border border-[#E5E7EB] bg-white p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#002FA7]">
                Pro operations snapshot
              </p>
              <h3 className="font-display text-2xl font-black mt-1">
                Usage and capacity at a glance.
              </h3>
              <p className="mt-2 text-sm text-[#525252] max-w-2xl">
                A quick visual view of your generations, websites/projects, team seats and API key capacity.
              </p>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">
              Live from plan usage
            </span>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {proMetrics.map((metric) => {
              const unlimited = metric.allowed === null || metric.allowed === undefined;
              const pctValue = unlimited ? 100 : percentOf(metric.used, metric.allowed);

              return (
                <div key={metric.label} className="border border-[#E5E7EB] bg-[#F5F5F5] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">
                        {metric.label}
                      </p>
                      <p className="font-display text-2xl font-black mt-1">
                        {metric.used}{unlimited ? "" : ` / ${metric.allowed}`}
                      </p>
                    </div>
                    <span className="text-[11px] text-[#737373] font-semibold">{metric.note}</span>
                  </div>
                  <div className="mt-4 h-2 bg-white overflow-hidden border border-[#E5E7EB]">
                    <div className="h-full bg-[#002FA7]" style={{ width: `${pctValue}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 border border-[#BFDBFE] bg-[#EFF6FF] p-4 text-sm text-[#1E40AF]">
            Pro is designed for active operations: workflow approvals, action tasks, LinkedIn/WordPress publishing, newsletter export, team seats and API access.
          </div>
        </div>
      )}

      <div className="lg:col-span-2">
        <ProjectsPanel usage={usage} refreshUsage={refreshUsage} />
      </div>
    </div>
  );
}

// ---------- Projects panel inside Plan & Usage ----------
function ProjectsPanel({ usage, refreshUsage }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    domain: "",
    project_type: "marketing",
    platform: "",
    target_location: "",
    description: "",
  });
  const [archiveModal, setArchiveModal] = useState({ open: false, project: null, restore: false });
  const [archiveLoading, setArchiveLoading] = useState(false);

  const planKey = normalizePlanKey(usage?.plan);
  const projectAllowed = projectLimitForUsage(usage);
  const activeProjects = projects.filter((project) => !isArchivedProject(project));
  const archivedProjects = projects.filter((project) => isArchivedProject(project));
  const activeCount = activeProjects.length;
  const atProjectLimit = projectAllowed !== null && projectAllowed !== undefined && activeCount >= Number(projectAllowed);

  const loadProjects = async () => {
    setLoading(true);

    try {
      const { data } = await axios.get(`${API}/projects`);
      setProjects(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      toast.error(getApiMessage(err, "Could not load projects."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const createProject = async (e) => {
    e.preventDefault();

    if (atProjectLimit) {
      toast.error(`Your ${planKey} plan already has ${activeCount}/${projectAllowed} active project slots used. Archive a project first or upgrade.`);
      return;
    }

    if (!form.name.trim()) {
      toast.error("Project name is required.");
      return;
    }

    setCreating(true);

    try {
      await axios.post(`${API}/projects`, {
        name: form.name.trim(),
        domain: form.domain.trim(),
        project_type: form.project_type.trim() || "marketing",
        platform: form.platform.trim(),
        target_location: form.target_location.trim(),
        description: form.description.trim(),
        status: "active",
      });

      toast.success("Project added.");
      setForm({ name: "", domain: "", project_type: "marketing", platform: "", target_location: "", description: "" });
      await loadProjects();
      if (refreshUsage) await refreshUsage();
    } catch (err) {
      toast.error(getApiMessage(err, "Could not create project."));
    } finally {
      setCreating(false);
    }
  };

  const askArchive = (project) => {
    setArchiveModal({ open: true, project, restore: false });
  };

  const askRestore = (project) => {
    setArchiveModal({ open: true, project, restore: true });
  };

  const closeArchiveModal = () => {
    setArchiveModal({ open: false, project: null, restore: false });
  };

  const confirmArchiveAction = async () => {
    const project = archiveModal.project;
    if (!project?.project_id) return;

    setArchiveLoading(true);

    try {
      const endpoint = archiveModal.restore ? "restore" : "archive";
      await axios.post(`${API}/projects/${project.project_id}/${endpoint}`);
      toast.success(archiveModal.restore ? "Project restored." : "Project archived.");
      closeArchiveModal();
      await loadProjects();
      if (refreshUsage) await refreshUsage();
    } catch (err) {
      toast.error(getApiMessage(err, archiveModal.restore ? "Could not restore project." : "Could not archive project."));
    } finally {
      setArchiveLoading(false);
    }
  };

  return (
    <>
      <div className="border border-[#E5E7EB] bg-white p-6 md:p-8" data-testid="projects-panel">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#002FA7]">
              Projects
            </p>
            <h3 className="font-display text-2xl font-black mt-1">
              Project slots for your plan
            </h3>
            <p className="mt-2 text-sm text-[#525252] max-w-2xl">
              Active projects count towards your plan limit. Archived projects stay visible for history, but cannot be used for new workflows, scheduling or publishing.
            </p>
          </div>

          <div className="border border-[#E5E7EB] bg-[#F5F5F5] px-5 py-4 min-w-[180px]">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">
              {planKey} project usage
            </p>
            <p className="font-display text-3xl font-black mt-1">
              {activeCount} / {projectAllowed ?? "∞"}
            </p>
            <p className="text-xs text-[#737373] mt-1">
              {archivedProjects.length} archived
            </p>
          </div>
        </div>

        <form onSubmit={createProject} className="mt-6 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="create-project-form">
          <input
            value={form.name}
            onChange={(e) => updateForm("name", e.target.value)}
            placeholder="Project name"
            className="input-line md:col-span-2"
            maxLength={120}
            disabled={creating || atProjectLimit}
            required
          />

          <input
            value={form.domain}
            onChange={(e) => updateForm("domain", e.target.value)}
            placeholder="Domain"
            className="input-line md:col-span-2"
            maxLength={160}
            disabled={creating || atProjectLimit}
          />

          <input
            value={form.target_location}
            onChange={(e) => updateForm("target_location", e.target.value)}
            placeholder="Target location"
            className="input-line"
            maxLength={120}
            disabled={creating || atProjectLimit}
          />

          <button
            type="submit"
            disabled={creating || atProjectLimit}
            className="btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="add-project-btn"
            title={atProjectLimit ? "Project limit reached. Archive an active project first." : "Add project"}
          >
            <Plus size={16} weight="bold" /> {creating ? "Adding..." : atProjectLimit ? "Limit reached" : "Add project"}
          </button>

          <div className="md:col-span-6">
            <textarea
              value={form.description}
              onChange={(e) => updateForm("description", e.target.value)}
              placeholder="Project description — what does this business/project do, who is it for, and what should AI understand before generating content?"
              className="input-line min-h-[104px] resize-y leading-relaxed"
              maxLength={1000}
              disabled={creating || atProjectLimit}
              data-testid="project-description-input"
            />

            <div className="mt-2 flex items-center justify-between gap-3 text-xs text-[#737373]">
              <span>
                Used as AI context for workflows, Social AI, WordPress drafts, LinkedIn posts and Strategy Maps.
              </span>
              <span className="font-mono">
                {form.description.length}/1000
              </span>
            </div>
          </div>
        </form>

        {atProjectLimit && (
          <div className="mt-4 border border-[#FECACA] bg-[#FEF2F2] p-4 text-sm text-[#B91C1C]">
            Your {planKey} plan is at {activeCount}/{projectAllowed} active projects. Archive one you are finished with to free a slot, or upgrade for more capacity.
          </div>
        )}

        <div className="mt-6 border border-[#E5E7EB]">
          <div className="px-4 py-3 border-b border-[#E5E7EB] bg-[#F5F5F5] flex items-center justify-between gap-3">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#737373]">
              All projects
            </p>
            <span className="text-xs text-[#737373]">
              Active: {activeCount} · Archived: {archivedProjects.length}
            </span>
          </div>

          {loading ? (
            <div className="p-4 grid gap-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-16 bg-[#F5F5F5] animate-pulse" />
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#525252]">
              No projects yet. Add your first project before creating workflows.
            </div>
          ) : (
            <ul className="divide-y divide-[#E5E7EB]">
              {projects.map((project) => {
                const archived = isArchivedProject(project);

                return (
                  <li key={project.project_id} className="px-4 py-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold truncate">{project.name}</p>
                        <span className={`border px-2 py-1 text-[10px] font-mono uppercase tracking-[0.16em] ${archived ? "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]" : "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]"}`}>
                          {archived ? "archived" : project.status || "active"}
                        </span>
                      </div>

                      <p className="mt-1 text-xs text-[#737373] font-mono truncate">
                        {project.domain || "No domain"} · {projectTypeLabel(project.project_type)}{project.target_location ? ` · ${project.target_location}` : ""}
                      </p>

                      {projectDescription(project) && (
                        <p className="mt-2 text-sm text-[#525252] leading-relaxed max-w-4xl">
                          {projectDescription(project)}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 shrink-0">
                      {archived ? (
                        <button
                          onClick={() => askRestore(project)}
                          disabled={atProjectLimit}
                          className="border border-[#002FA7] text-[#002FA7] px-3 py-2 text-xs font-bold hover:bg-[#EFF6FF] disabled:opacity-50"
                          title={atProjectLimit ? "Archive another active project before restoring this one." : "Restore project"}
                        >
                          Restore
                        </button>
                      ) : (
                        <button
                          onClick={() => askArchive(project)}
                          className="border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-[#F5F5F5] inline-flex items-center gap-1.5"
                        >
                          <Archive size={14} weight="bold" /> Archive
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <ConfirmModal
        open={archiveModal.open}
        title={archiveModal.restore ? "Restore project" : "Archive project"}
        message={archiveModal.restore
          ? `Restore ${archiveModal.project?.name || "this project"} as an active project? It will count towards your ${planKey} project limit again.`
          : `Archive ${archiveModal.project?.name || "this project"}? Old history stays visible, but workflows and publishing for this project will be view-only.`}
        confirmLabel={archiveModal.restore ? "Restore project" : "Archive project"}
        loading={archiveLoading}
        onClose={closeArchiveModal}
        onConfirm={confirmArchiveAction}
      />
    </>
  );
}

// ---------- Team tab ----------
function TeamTab({ usage }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [freshInviteLink, setFreshInviteLink] = useState(null);

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: null,
    id: null,
    title: "",
    message: "",
    confirmLabel: "Confirm",
  });

  const [confirmLoading, setConfirmLoading] = useState(false);

  const closeConfirmModal = () => {
    setConfirmModal({
      open: false,
      type: null,
      id: null,
      title: "",
      message: "",
      confirmLabel: "Confirm",
    });
  };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/org`);
      setData(data);
    } catch (e) {
      // /org should usually be available for authenticated users.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);

    try {
      const { data } = await axios.post(`${API}/org/invites`, {
        email: inviteEmail,
      });

      setFreshInviteLink(data.invite_link);
      toast.success(data.email_sent ? "Invite emailed." : "Invite created. Share the link below.");
      setInviteEmail("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || "Could not send invite");
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (inviteId) => {
    setConfirmModal({
      open: true,
      type: "revoke_invite",
      id: inviteId,
      title: "Revoke invite",
      message:
        "This invite link will stop working immediately. The invited person will no longer be able to join your workspace using this invite.",
      confirmLabel: "Revoke invite",
    });
  };

  const removeMember = async (userId) => {
    setConfirmModal({
      open: true,
      type: "remove_member",
      id: userId,
      title: "Remove member",
      message:
        "This member will lose access to your Ordeneli workspace. If your backend downgrade logic is enabled, their workspace access is removed and their account returns to Starter outside this organisation.",
      confirmLabel: "Remove member",
    });
  };

  const confirmTeamAction = async () => {
    if (!confirmModal.type || !confirmModal.id) return;

    setConfirmLoading(true);

    try {
      if (confirmModal.type === "revoke_invite") {
        await axios.delete(`${API}/org/invites/${confirmModal.id}`);
        toast.success("Invite revoked.");
      }

      if (confirmModal.type === "remove_member") {
        await axios.delete(`${API}/org/members/${confirmModal.id}`);
        toast.success("Member removed.");
      }

      closeConfirmModal();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || "Action failed");
    } finally {
      setConfirmLoading(false);
    }
  };

  if (loading) return <div className="h-24 bg-[#F5F5F5] animate-pulse" />;
  if (!data) return null;

  const isOwner = data.org.owner_user_id === user?.user_id;
  const plan = data.org.plan;
  const canInvite =
    plan === "pro" &&
    isOwner &&
    data.seats.used + data.invites.length < data.seats.allowed;

  if (plan !== "pro") {
    return (
      <div className="border border-[#E5E7EB] bg-white p-8" data-testid="team-locked">
        <Users size={28} weight="duotone" className="text-[#002FA7]" />
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">
          Pro feature
        </p>
        <h2 className="mt-1 font-display text-2xl font-black">
          Team seats are on the Pro plan.
        </h2>
        <p className="mt-3 text-[#525252]">
          Upgrade to Pro to invite teammates, each with their own account and private history.
        </p>
        <Link to="/pricing" className="btn-primary mt-5 inline-flex">
          Upgrade to Pro <ArrowUpRight size={16} weight="bold" />
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="team-tab">
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-[#E5E7EB] bg-white">
            <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
                Members · {data.seats.used}/{data.seats.allowed}
              </p>
            </div>

            <ul className="divide-y divide-[#E5E7EB]">
              {data.members.map((m) => (
                <li
                  key={m.user_id}
                  className="px-6 py-4 flex items-center justify-between gap-3"
                  data-testid={`member-${m.user_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {m.picture ? (
                      <img
                        src={m.picture}
                        alt=""
                        className="h-9 w-9 object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="h-9 w-9 bg-[#0A0A0A] text-white flex items-center justify-center font-semibold text-sm">
                        {(m.name || m.email || "?").charAt(0).toUpperCase()}
                      </div>
                    )}

                    <div className="min-w-0">
                      <p className="font-semibold truncate">{m.name}</p>
                      <p className="text-xs text-[#737373] truncate">{m.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">
                      {m.role}
                    </span>

                    {isOwner && m.role !== "owner" && (
                      <button
                        onClick={() => removeMember(m.user_id)}
                        className="text-[#DC2626] hover:bg-[#FEF2F2] p-2"
                        data-testid={`remove-${m.user_id}`}
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {isOwner && data.invites.length > 0 && (
            <div className="border border-[#E5E7EB] bg-white">
              <div className="px-6 py-4 border-b border-[#E5E7EB]">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
                  Pending invites
                </p>
              </div>

              <ul className="divide-y divide-[#E5E7EB]">
                {data.invites.map((i) => (
                  <li
                    key={i.invite_id}
                    className="px-6 py-4 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{i.email}</p>
                      <p className="text-xs text-[#737373] truncate">
                        Invited {new Date(i.created_at).toLocaleString()}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const link = `${window.location.origin}/org/join?token=${i.token}`;
                          navigator.clipboard.writeText(link);
                          toast.success("Invite link copied");
                        }}
                        className="text-sm inline-flex items-center gap-1 font-semibold text-[#002FA7]"
                      >
                        <Copy size={14} weight="bold" /> Copy link
                      </button>

                      {isOwner && (
                        <button
                          onClick={() => revoke(i.invite_id)}
                          className="text-[#DC2626] hover:bg-[#FEF2F2] p-2"
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="space-y-5">
          {isOwner ? (
            <div className="border border-[#E5E7EB] bg-white p-6">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
                Invite a teammate
              </p>

              <p className="mt-2 text-sm text-[#525252]">
                They'll get their own workspace seat. The invite opens the registration/join page where they create their email and password.
              </p>

              <form onSubmit={invite} className="mt-4 flex flex-col gap-3" data-testid="invite-form">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="teammate@company.com"
                  className="input-line"
                  required
                  data-testid="invite-email-input"
                />

                <button
                  type="submit"
                  disabled={!canInvite || inviting}
                  className="btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="invite-submit"
                >
                  {inviting ? (
                    "Sending…"
                  ) : (
                    <>
                      <Plus size={16} weight="bold" /> Send invite
                    </>
                  )}
                </button>
              </form>

              {!canInvite && data.seats.used + data.invites.length >= data.seats.allowed && (
                <p className="mt-3 text-xs text-[#DC2626]">
                  All {data.seats.allowed} seats are full.
                </p>
              )}

              {freshInviteLink && (
                <div className="mt-5 border-t border-[#E5E7EB] pt-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">
                    Share link
                  </p>

                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 text-xs bg-[#F5F5F5] p-2 break-all">
                      {freshInviteLink}
                    </code>

                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(freshInviteLink);
                        toast.success("Copied");
                      }}
                      className="btn-secondary !p-2"
                      data-testid="copy-invite-link"
                    >
                      <Copy size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="border border-[#E5E7EB] bg-[#F5F5F5] p-6">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
                Team access
              </p>

              <h3 className="font-display text-xl font-black mt-1">
                Member access
              </h3>

              <p className="mt-2 text-sm text-[#525252] leading-relaxed">
                You are a member of this workspace. Only the workspace owner can invite new teammates, revoke invites, or remove members.
              </p>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        danger
        loading={confirmLoading}
        onClose={closeConfirmModal}
        onConfirm={confirmTeamAction}
      />
    </>
  );
}

// ---------- API Keys tab ----------
function ApiTab({ usage }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [freshKey, setFreshKey] = useState(null);
  const [locked, setLocked] = useState(false);

  const [revokeModal, setRevokeModal] = useState({
    open: false,
    keyId: null,
  });

  const [revokeLoading, setRevokeLoading] = useState(false);

  const closeRevokeModal = () => {
    setRevokeModal({
      open: false,
      keyId: null,
    });
  };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/me/api-keys`);
      setKeys(data.items || []);
    } catch (e) {
      if (e?.response?.status === 403) setLocked(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const { data } = await axios.post(`${API}/me/api-keys`, { label });
      setFreshKey(data);
      setLabel("");
      toast.success("API key created. Copy it now — it won't be shown again.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || "Could not create key");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (keyId) => {
    setRevokeModal({
      open: true,
      keyId,
    });
  };

  const confirmRevokeKey = async () => {
    if (!revokeModal.keyId) return;

    setRevokeLoading(true);

    try {
      await axios.delete(`${API}/me/api-keys/${revokeModal.keyId}`);
      toast.success("Key revoked.");
      closeRevokeModal();
      await load();
    } catch {
      toast.error("Could not revoke");
    } finally {
      setRevokeLoading(false);
    }
  };

  if (loading) return <div className="h-24 bg-[#F5F5F5] animate-pulse" />;

  const activeKeyCount = keys.filter((k) => !k.revoked_at).length;
  const keyLimit = firstNumber(usage?.api_keys?.allowed, usage?.api_key_limit, usageLimitValue(usage, "api_key_limit", 3)) ?? 3;
  const atKeyLimit = activeKeyCount >= keyLimit;

  if (locked || !usage?.api_access) {
    return (
      <div className="border border-[#E5E7EB] bg-white p-8" data-testid="api-locked">
        <Key size={28} weight="duotone" className="text-[#002FA7]" />

        <p className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">
          Pro feature
        </p>

        <h2 className="mt-1 font-display text-2xl font-black">
          API access is on the Pro plan.
        </h2>

        <p className="mt-3 text-[#525252]">
          Programmatically call every tool with your own keys. Upgrade to Pro to enable.
        </p>

        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/pricing" className="btn-primary inline-flex">
            Upgrade to Pro <ArrowUpRight size={16} weight="bold" />
          </Link>

          <Link to="/docs/api" className="btn-secondary inline-flex" data-testid="locked-api-docs-link">
            Preview the API docs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="api-tab">
        <div className="lg:col-span-2 border border-[#E5E7EB] bg-white">
          <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between gap-3 flex-wrap">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
              Your API keys · {activeKeyCount}/{keyLimit} active
            </p>

            <Link
              to="/docs/api"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] hover:text-[#0A0A0A]"
              data-testid="settings-api-docs-link"
            >
              Read API docs <ArrowUpRight size={12} weight="bold" />
            </Link>
          </div>

          {keys.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Key size={28} weight="duotone" className="mx-auto text-[#737373]" />
              <p className="mt-3 text-[#525252]">
                No keys yet. Create one to start calling the API.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[#E5E7EB]">
              {keys.map((k) => (
                <li
                  key={k.key_id}
                  className="px-6 py-4 flex items-center justify-between gap-3"
                  data-testid={`apikey-${k.key_id}`}
                >
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{k.label}</p>

                    <p className="text-xs text-[#737373] font-mono truncate">
                      {k.key_prefix}…{k.key_last4}
                    </p>

                    <p className="text-[10px] text-[#737373] font-mono mt-1">
                      Created {new Date(k.created_at).toLocaleDateString()}
                      {k.last_used_at && ` · last used ${new Date(k.last_used_at).toLocaleDateString()}`}
                      {k.revoked_at && ` · revoked`}
                    </p>
                  </div>

                  {!k.revoked_at && (
                    <button
                      onClick={() => revoke(k.key_id)}
                      className="text-[#DC2626] hover:bg-[#FEF2F2] p-2"
                      data-testid={`revoke-apikey-${k.key_id}`}
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-5">
          <div className="border border-[#E5E7EB] bg-white p-6">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
              Create a key
            </p>

            <form onSubmit={create} className="mt-4 flex flex-col gap-3" data-testid="create-apikey-form">
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Production backend"
                className="input-line"
                required
                maxLength={64}
                data-testid="apikey-label-input"
              />

              <button
                type="submit"
                disabled={creating || atKeyLimit}
                className="btn-primary justify-center disabled:opacity-50"
                data-testid="create-apikey-btn"
              >
                {creating ? (
                  "Creating…"
                ) : (
                  <>
                    <Plus size={16} weight="bold" /> {atKeyLimit ? "Key limit reached" : "Generate key"}
                  </>
                )}
              </button>
            </form>

            {atKeyLimit && (
              <p className="mt-3 text-xs text-[#DC2626]">
                Pro includes {keyLimit} active API key{keyLimit === 1 ? "" : "s"}. Revoke an old key before creating another.
              </p>
            )}

            {freshKey && (
              <div className="mt-5 border-t border-[#E5E7EB] pt-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#DC2626]">
                  Copy now — shown only once
                </p>

                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 text-xs bg-[#F5F5F5] p-2 break-all font-mono">
                    {freshKey.key}
                  </code>

                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(freshKey.key);
                      toast.success("Copied");
                    }}
                    className="btn-primary !p-2"
                    data-testid="copy-fresh-key"
                  >
                    <Copy size={14} weight="bold" />
                  </button>
                </div>

                <button
                  onClick={() => setFreshKey(null)}
                  className="mt-2 text-xs text-[#737373] underline"
                >
                  Dismiss
                </button>
              </div>
            )}
          </div>

          <div className="border border-[#E5E7EB] bg-[#F5F5F5] p-6 text-sm text-[#525252]">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373] mb-3">
              Usage
            </p>

            <p>
              Send{" "}
              <code className="bg-white px-1">
                Authorization: ApiKey &lt;your-key&gt;
              </code>{" "}
              on any request to{" "}
              <code className="bg-white px-1">/api/generate</code>. Same plan
              limits apply. API access is Pro only and includes a limited number of active keys.
            </p>

            <Link
              to="/docs/api"
              className="mt-4 inline-flex items-center gap-1.5 text-[#002FA7] font-semibold hover:text-[#0A0A0A]"
              data-testid="settings-api-docs-cta"
            >
              Full API documentation <ArrowUpRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={revokeModal.open}
        title="Revoke API key"
        message="This API key will stop working immediately. Any app, integration, or automation using it will lose access."
        confirmLabel="Revoke key"
        danger
        loading={revokeLoading}
        onClose={closeRevokeModal}
        onConfirm={confirmRevokeKey}
      />
    </>
  );
}

// ---------- Account tab ----------
function AccountTab({ user }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isPasswordAccount = (user?.provider || "password") === "password";

  const submit = async (e) => {
    e.preventDefault();

    if (newPw.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }

    if (newPw !== confirmPw) {
      toast.error("Passwords don't match.");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/me/password`, {
        current_password: currentPw,
        new_password: newPw,
      });

      toast.success("Password updated.");
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      toast.error(typeof detail === "string" ? detail : "Couldn't update password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="account-tab">
      <div className="lg:col-span-2 border border-[#E5E7EB] bg-white p-6 sm:p-8">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
          Security
        </p>

        <h2 className="mt-1 font-display text-2xl font-black">
          Change your password
        </h2>

        {!isPasswordAccount ? (
          <div className="mt-5 border border-[#FDE68A] bg-[#FFFBEB] p-4 text-sm text-[#92400E]" data-testid="account-social-notice">
            You're signed in with{" "}
            <strong className="capitalize">{user?.provider}</strong>. Password
            changes are managed by your identity provider.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 flex flex-col gap-4 max-w-md" data-testid="change-password-form">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold">
                Current password
              </label>
              <input
                type="password"
                className="input-line"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="current-password-input"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold">
                New password
              </label>
              <input
                type="password"
                className="input-line"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                minLength={8}
                required
                autoComplete="new-password"
                data-testid="new-password-input"
              />
              <p className="text-xs text-[#737373]">
                Minimum 8 characters.
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold">
                Confirm new password
              </label>
              <input
                type="password"
                className="input-line"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                minLength={8}
                required
                autoComplete="new-password"
                data-testid="confirm-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary justify-center self-start mt-2"
              data-testid="change-password-submit"
            >
              {submitting ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>

      <div className="border border-[#E5E7EB] bg-[#F5F5F5] p-6 text-sm text-[#525252]">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373] mb-3">
          Account details
        </p>

        <dl className="space-y-3">
          <div>
            <dt className="text-[11px] font-mono uppercase tracking-wide text-[#737373]">
              Email
            </dt>
            <dd className="text-[#0A0A0A] font-semibold break-all">
              {user?.email}
            </dd>
          </div>

          <div>
            <dt className="text-[11px] font-mono uppercase tracking-wide text-[#737373]">
              Sign-in method
            </dt>
            <dd className="text-[#0A0A0A] font-semibold capitalize">
              {user?.provider || "password"}
            </dd>
          </div>

          <div>
            <dt className="text-[11px] font-mono uppercase tracking-wide text-[#737373]">
              Display name
            </dt>
            <dd className="text-[#0A0A0A] font-semibold">
              {user?.name || "—"}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}